// Preserve microsecond ordering: Date.parse alone rounds to milliseconds.
function compareTimestamps(left, right) {
    const milliseconds = Date.parse(left) - Date.parse(right);
    if (milliseconds !== 0) return milliseconds;
    const fraction = value => (value.match(/\.(\d+)/)?.[1] || "").padEnd(6, "0");
    return fraction(left).localeCompare(fraction(right));
}

/**
 * Select the latest valid price-update metadata from a snapshot or realtime event.
 *
 * Snapshots and events can arrive out of order. Missing/invalid timestamps and
 * older updates preserve the current value; equal or newer timestamps replace it.
 * Comparison includes microseconds so updates within one millisecond stay ordered.
 *
 * Return only lastModifiedOn for customers. Administrators also receive
 * lastModifiedBy (null when unavailable). This strips updater details even when
 * retaining previously stored metadata after a role change; server authorization
 * remains responsible for excluding those details from customer payloads.
 *
 * This returns a new object (or null when no valid update is available), without
 * mutating either input. It selects one complete update rather than combining
 * fields from different updates, keeping the timestamp and updater together.
 *
 * @param {Object|null} current Previously accepted metadata, with a valid timestamp.
 * @param {Object|null|undefined} incoming Metadata from a snapshot or realtime event.
 * @param {boolean} isAdmin Whether the current user may retain updater details.
 * @returns {Object|null} Selected metadata restricted to the current user's role.
 */
export function mergeLastPriceUpdate(current, incoming, isAdmin) {
    const clean = value => value ? {
        lastModifiedOn: value.lastModifiedOn,
        ...(isAdmin ? { lastModifiedBy: value.lastModifiedBy ?? null } : {}),
    } : null;

    if (!incoming?.lastModifiedOn || !Number.isFinite(Date.parse(incoming.lastModifiedOn))) {
        return clean(current);
    }
    if (current?.lastModifiedOn && compareTimestamps(incoming.lastModifiedOn, current.lastModifiedOn) < 0) {
        return clean(current);
    }
    return clean(incoming);
}
